import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import spaceImage from '../images/space.webp';
import planetImage from '../images/planet.webp';
import choosePuzzleImage from '../images/screenshots/choose_puzzle.webp';
import mobileImage from '../images/screenshots/mobile.jpg';
import multiplayerImage from '../images/screenshots/multiplayer.webp';
import puzzlePlanetLogo from '../images/logos/title.webp';
import mindfulLogo from '../images/logos/mindful-logo.svg';
import { authApi } from '../services/api';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { TrialSignupErrorCode } from '../types/auth';
import Spinner from './Spinner';

const TrialSignup: React.FC = () => {
    const [email, setEmail] = useState('');
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { getToken, isLoaded } = useRecaptcha();

    const carouselImages = [
        { src: choosePuzzleImage, caption: 'Choose from a variety of puzzles' },
        { src: multiplayerImage, caption: 'Play together with your friends' },
        { src: mobileImage, caption: 'Play on any device' },
    ];

    // Auto-cycle carousel
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex(prev => (prev + 1) % carouselImages.length);
        }, 5000); // Change image every 5 seconds

        return () => clearInterval(interval);
    }, [carouselImages.length]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        if (!isLoaded) {
            setError('Security verification is loading. Please wait a moment and try again.');
            setIsLoading(false);
            return;
        }

        try {
            const recaptchaToken = await getToken('email_signup');
            const response = await authApi.startTrialEmailSignup({
                email: email.trim(),
                recaptchaToken,
            });

            if (response.status === 'success') {
                navigate('/trial-signup-link', { state: { email: email.trim() } });
            } else {
                if (response.code === TrialSignupErrorCode.TRIAL_SIGNUPS_DISABLED) {
                    setError('TRIAL_SIGNUPS_DISABLED');
                } else if (response.code === TrialSignupErrorCode.PLAYER_ALREADY_EXISTS) {
                    setError('PLAYER_ALREADY_EXISTS');
                } else {
                    setError(response.message || 'Failed to send signup email. Please try again.');
                }
            }
        } catch (err: any) {
            console.error('Email signup error:', err);
            if (err.message && err.message.includes('reCAPTCHA')) {
                setError('Security verification failed. Please try again.');
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Loading Modal */}
            {isLoading && (
                <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
                    <div className='bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center'>
                        <div className='mb-6'>
                            <div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                                <Spinner size='lg' color='blue' />
                            </div>
                        </div>
                        <h2 className='text-2xl font-bold text-gray-900 mb-2'>
                            Sending Signup Email
                        </h2>
                        <p className='text-gray-600 mb-4'>
                            Please wait while we process your request
                        </p>
                        <div className='text-sm text-gray-500'>This may take a few moments...</div>
                    </div>
                </div>
            )}

            <div
                className='min-h-screen flex flex-col items-center justify-start py-12 px-4 overflow-x-hidden w-full'
                style={{
                    backgroundImage: `url(${spaceImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className='flex flex-col items-center space-y-8 max-w-5xl w-full'>
                    {/* Planet Image */}
                    <div className='flex justify-center'>
                        <img
                            src={planetImage}
                            alt='Planet'
                            className='max-w-md md:max-w-2xl lg:max-w-3xl'
                        />
                    </div>

                    {/* Heading */}
                    <h1
                        className='text-4xl md:text-5xl font-bold text-center'
                        style={{ color: '#FFB502' }}
                    >
                        Your puzzle journey is about to begin!
                    </h1>

                    {/* Trial Details */}
                    <p className='text-xl md:text-2xl text-center' style={{ color: '#FFFFFF' }}>
                        Sign up now for a 7-day free trial. $3/month after that.<br></br> Cancel
                        anytime.
                    </p>

                    {/* Email Form */}
                    <form onSubmit={handleSubmit} className='w-full max-w-2xl'>
                        <div className='flex flex-col sm:flex-row gap-4 items-center'>
                            <input
                                type='email'
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder='Email Address'
                                required
                                disabled={isLoading}
                                className='flex-1 w-full px-6 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200 bg-white text-gray-900 placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
                            />
                            <button
                                type='submit'
                                disabled={isLoading}
                                className='w-full sm:w-auto px-8 py-4 text-lg font-semibold bg-gradient-to-b from-yellow-400 to-orange-500 text-black rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                                {isLoading ? 'Sending...' : 'Get Started'}
                            </button>
                        </div>
                        <p className='mt-4 text-center text-white text-sm'>
                            If you have any issues during signup, please email{' '}
                            <a
                                href='mailto:support@mindfulgames.io'
                                className='text-orange-400 hover:text-orange-300 underline transition-colors duration-200'
                            >
                                support@mindfulgames.io
                            </a>
                        </p>
                        {error && (
                            <div className='mt-4 text-center text-red-400 text-sm'>
                                {error === 'TRIAL_SIGNUPS_DISABLED' ? (
                                    <div>
                                        Trial signups are currently not active, but you can sign up
                                        for a subscription right now{' '}
                                        <Link
                                            to='/signup-new'
                                            className='underline hover:text-orange-400 transition-colors duration-200'
                                        >
                                            here
                                        </Link>
                                        .
                                    </div>
                                ) : error === 'PLAYER_ALREADY_EXISTS' ? (
                                    <div>
                                        An account already exists with this email address.{' '}
                                        <Link
                                            to='/login'
                                            className='underline hover:text-orange-400 transition-colors duration-200'
                                        >
                                            Please log in here
                                        </Link>
                                        .
                                    </div>
                                ) : (
                                    error
                                )}
                            </div>
                        )}
                    </form>
                </div>

                {/* Horizontal Divider */}
                <hr
                    className='w-full border-t mt-16 md:mt-20 mb-16 md:mb-20'
                    style={{ borderColor: '#FFFFFF' }}
                />

                <div className='flex flex-col items-center space-y-8 max-w-5xl w-full'>
                    {/* Enjoy Text */}
                    <p
                        className='text-4xl md:text-5xl font-bold text-center mt-16 md:mt-20'
                        style={{ color: '#FFB502' }}
                    >
                        Enjoy puzzles from around the planet, with people from around the planet.
                    </p>

                    {/* Image Carousel */}
                    <div className='w-full max-w-4xl mt-8'>
                        <div className='relative'>
                            {/* Translucent Box Container */}
                            <div className='bg-black/30 backdrop-blur-sm rounded-lg p-6 md:p-8'>
                                {/* Carousel Image */}
                                <div className='flex justify-center items-center h-96 md:h-[500px]'>
                                    <img
                                        src={carouselImages[currentImageIndex].src}
                                        alt={`Screenshot ${currentImageIndex + 1}`}
                                        className='max-w-full max-h-full w-auto h-full object-contain rounded-lg shadow-lg'
                                    />
                                </div>

                                {/* Dots Indicator */}
                                <div className='flex justify-center gap-2 mt-4'>
                                    {carouselImages.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentImageIndex(index)}
                                            className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                                index === currentImageIndex
                                                    ? 'bg-white'
                                                    : 'bg-white/50 hover:bg-white/75'
                                            }`}
                                            aria-label={`Go to image ${index + 1}`}
                                        />
                                    ))}
                                </div>

                                {/* Caption */}
                                <p
                                    className='text-lg md:text-xl text-center mt-4'
                                    style={{ color: '#FFB502' }}
                                >
                                    {carouselImages[currentImageIndex].caption}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Features Heading */}
                    <h2
                        className='text-4xl md:text-5xl font-bold text-center mt-12'
                        style={{ color: '#FFB502' }}
                    >
                        Features
                    </h2>

                    {/* Bulleted List */}
                    <ul className='mt-8 space-y-4 text-lg md:text-xl' style={{ color: '#FFFFFF' }}>
                        <li className='flex items-start'>
                            <span className='mr-3'>•</span>
                            <span>
                                Choose from over 1,000 puzzle locations. New locations are added
                                regularly.
                            </span>
                        </li>
                        <li className='flex items-start'>
                            <span className='mr-3'>•</span>
                            <span>Create a tour and invite up to five of your friends.</span>
                        </li>
                        <li className='flex items-start'>
                            <span className='mr-3'>•</span>
                            <span>Your friends play for free!</span>
                        </li>
                        <li className='flex items-start'>
                            <span className='mr-3'>•</span>
                            <span>Select puzzle difficulty from 100 to 1,000 pieces.</span>
                        </li>
                        <li className='flex items-start'>
                            <span className='mr-3'>•</span>
                            <span>
                                Endless replayability - every playthrough of a puzzle is unique.
                            </span>
                        </li>
                    </ul>
                </div>

                {/* Footer */}
                <footer className='w-full mt-20 py-12 px-4'>
                    <div className='max-w-6xl mx-auto'>
                        {/* Orange Horizontal Line */}
                        <hr className='w-full border-t mb-8' style={{ borderColor: '#FFB502' }} />

                        {/* Logos */}
                        <div className='flex flex-row items-center justify-center gap-3 md:gap-8 mb-8 px-4 w-full'>
                            <a
                                href='https://puzzleplanet.io'
                                target='_blank'
                                rel='noopener noreferrer'
                                className='hover:opacity-80 transition-opacity duration-200 flex-1 flex justify-end items-center pr-2'
                            >
                                <img
                                    src={puzzlePlanetLogo}
                                    alt='Puzzle Planet'
                                    className='h-12 md:h-20 w-auto max-w-full object-contain'
                                />
                            </a>
                            <div
                                className='w-px h-12 md:h-20 flex-shrink-0'
                                style={{ backgroundColor: '#FFB502' }}
                            />
                            <a
                                href='https://mindfulgames.io'
                                target='_blank'
                                rel='noopener noreferrer'
                                className='hover:opacity-80 transition-opacity duration-200 flex-1 flex justify-start items-center pl-2'
                            >
                                <img
                                    src={mindfulLogo}
                                    alt='Mindful Games'
                                    className='h-10 md:h-16 w-auto max-w-full object-contain'
                                />
                            </a>
                        </div>

                        {/* Navigation Links */}
                        <div className='flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-6'>
                            <a
                                href='https://puzzleplanet.io/privacy?returnUrl=blank'
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-white hover:text-orange-400 transition-colors duration-200'
                            >
                                Privacy Policy
                            </a>
                            <a
                                href='https://puzzleplanet.io/terms?returnUrl=blank'
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-white hover:text-orange-400 transition-colors duration-200'
                            >
                                Terms of Service
                            </a>
                            <a
                                href='https://puzzleplanet.io/acknowledgments?returnUrl=blank'
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-white hover:text-orange-400 transition-colors duration-200'
                            >
                                Acknowledgments
                            </a>
                        </div>

                        {/* Copyright */}
                        <p className='text-center text-white text-sm'>
                            © 2025 Puzzle Planet. All rights reserved.
                        </p>
                    </div>
                </footer>
            </div>
        </>
    );
};

export default TrialSignup;
